import { List, Map } from 'immutable';
import { getTextStylesFromBrandStyle } from 'modules/BrandDefinition';
import { isValidHeadingTag } from 'modules/Lexical/Plugins/HeadingTagPlugin/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IMap } from 'typings/DeepIMap';
import * as Constants from 'const';
import * as Models from 'models';
import { getChangedStylesToBeKept } from 'utils/styles/getStylesToBeKept';
import { getPrioritizedRelation } from '../utils/relation';
import { Styles, StylesSetters, getInitialStyles, stylesFromSource } from '../utils/styles';

const emptyRelationStyles = Map();

type HookProps = {
  relation: Models.LayeredRegularRelationMap<Models.TextRelationStyles>;
  activeLayer: Constants.Layer;
  isAutoFitContent: boolean;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  brandStyles: Models.BrandStylesMap;
};

function getSource(props: Pick<HookProps, 'relation' | 'activeLayer'>): IMap<Models.TextRelationStyles> {
  const { relation, activeLayer } = props;

  return relation.getIn(['styles', activeLayer]) || emptyRelationStyles;
}

type StylesHook = {
  styles: Styles;
  stylesSetters: StylesSetters;
  ensureStylesToBeKept: () => void;
  applyBrandStyle: (
    brandStyle: Models.BrandStyleMap,
    brandStyleValues: Models.TextBrandStyles,
  ) => void;
  hasBrandStyleHeadingTag: boolean;
};

export default function useStyles(props: HookProps): StylesHook {
  const prevPropsRef = useRef(props);
  const {
    relation, activeLayer,
    colors, fonts, brandStyles,
  } = props;

  const source = getSource(props);
  const [styles, setStyles] = useState<Styles>(() => getInitialStyles(
    source, colors, fonts, brandStyles,
  ));
  const { textAlign } = getTextStylesFromBrandStyle(styles.brandStyle, List([]), List([]));
  const isListBrandStyle = textAlign === Constants.TextHorizontalAlignmentType.UNORDERED_LIST;
  const hasBrandStyleHeadingTag = isValidHeadingTag(styles.brandStyle?.get('tag')) && !isListBrandStyle;

  const setters = useMemo((): StylesSetters => {
    return {
      backgroundColor: color => setStyles(prev => ({
        ...prev,
        backgroundColor: color,
        backgroundGradient: undefined,
        brandStyleChanged: true,
      })),
      backgroundColorOpacity: backgroundColorOpacity => setStyles(prev => ({
        ...prev,
        backgroundColorOpacity,
        brandStyleChanged: true,
      })),
      backgroundGradient: (gradient, backupColor) => setStyles(prev => ({
        ...prev,
        backgroundColor: backupColor,
        backgroundGradient: gradient,
        brandStyleChanged: true,
      })),
      backgroundImage: backgroundImage => setStyles(prev => ({ ...prev, backgroundImage })),
      border: border => setStyles(prev => ({ ...prev, border })),
      borderRadius: borderRadius => setStyles(prev => ({ ...prev, borderRadius })),
      brandStyle: (brandStyle, iterator?: number) => setStyles(prev => ({
        ...prev,
        brandStyle,
        brandStyleVersion: iterator ?? prev.brandStyleVersion + 1,
      })),
      brandStyleChanged: (value = true) => setStyles(prev => ({ ...prev, brandStyleChanged: value })),
      padding: padding => setStyles(prev => ({
        ...prev,
        padding,
        brandStyleChanged: true,
      })),
      verticalAlignment: verticalAlignment => setStyles(prev => ({
        ...prev,
        verticalAlignment,
        brandStyleChanged: true,
      })),
    };
  }, []);

  // TODO: it looks like overhead
  const ensureStylesToBeKept = useCallback((): void => {
    const newStyles = stylesFromSource(
      relation.getIn(['styles', activeLayer]),
      brandStyles,
      colors,
    );

    const {
      backgroundColor,
      backgroundColorTint,
      backgroundColorOpacity,
      backgroundGradient,
      backgroundImage,
      border,
      padding,
    } = getChangedStylesToBeKept(
      getPrioritizedRelation({ relation, activeLayer }),
      brandStyles,
      colors,
      fonts,
    );

    if (backgroundGradient) {
      setters.backgroundGradient(newStyles?.backgroundGradient, newStyles?.backgroundColor);
    } else if (backgroundColor || backgroundColorTint) {
      setters.backgroundColor(newStyles?.backgroundColor);
    }

    if (backgroundColorOpacity) {
      setters.backgroundColorOpacity(backgroundColorOpacity);
    }

    if (backgroundImage) {
      setters.backgroundImage(newStyles?.backgroundImage);
    }

    if (border) {
      setters.border(newStyles?.border);
    }

    if (padding) {
      setters.padding(newStyles?.padding);
    }
  }, [relation, activeLayer, fonts, colors, brandStyles, setters]);

  const applyBrandStyle = useCallback((
    brandStyle: Models.BrandStyleMap,
    brandStyleValues: Models.TextBrandStyles,
  ) => setStyles(prev => ({
    ...prev,
    padding: brandStyleValues.padding,
    verticalAlignment: brandStyleValues.verticalAlignment,
    backgroundColor: brandStyleValues.backgroundColor,
    backgroundGradient: undefined,
    brandStyle,
    brandStyleVersion: prev.brandStyleVersion + 1,
    brandStyleChanged: false,
  }),
  ), [setStyles]);

  useEffect(() => {
    // skip run on mount
    if (prevPropsRef.current === props) {
      return;
    }

    /* we are updating padding in the store here with the cellHook.resetHeight(relationsWithUpdatedStyles):
     src\components\ArtboardAssets\Text\hooks\usePaddingEffect.ts,
    while all other values in the source are not updated, so need to omit it here */
    const sourceWithoutPadding = source.delete('padding');

    // we check that the styles is changed without autofit content as in some cases it causes unnecessary triggers of state sync
    const preparedCurrentStyles = sourceWithoutPadding.has('isAutoFitContent')
      ? sourceWithoutPadding.delete('isAutoFitContent')
      : sourceWithoutPadding;
    const prevSourceWithoutPadding = getSource(prevPropsRef.current).delete('padding');
    const preparedPrevStyles = prevSourceWithoutPadding.has('isAutoFitContent')
      ? prevSourceWithoutPadding.delete('isAutoFitContent')
      : prevSourceWithoutPadding;

    if (preparedCurrentStyles.equals(preparedPrevStyles)) {
      return;
    }

    setStyles((prev) => {
      const brandStyleChanged = source.get('brandStyleChanged') || prev.brandStyleChanged;

      return {
        ...stylesFromSource(source, brandStyles, colors),
        brandStyle: brandStyles.get(source.get('brandStyleId') || Constants.Styles.DefaultTextBrandStyle),
        brandStyleVersion: prev.brandStyleVersion + 1,
        brandStyleChanged,
      };
    });
  }, [source, relation.get('id'), activeLayer]);

  useEffect(() => {
    prevPropsRef.current = props;
  });

  return {
    styles,
    stylesSetters: setters,
    ensureStylesToBeKept,
    applyBrandStyle,
    hasBrandStyleHeadingTag,
  };
}
